import { Accordion, Box, Column, Copy, Row, useAccordion } from '@honda-canada/design-system-react';
import { compiler } from 'markdown-to-jsx';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_COMPARE_ELEMENTS, SPECS_ACCORDION_EMPTY_CONTENT } from '../../../utils/constants';
import { stripMarkdownHeading } from '../../../utils/markdown';
import themeStyles from '../styles/SpecificationsAccordion.styles';

const Wrapper = themeStyles.apply(Box, 'Wrapper');
const Content = themeStyles.apply(Box, 'Content');
const ContentRow = themeStyles.apply(Row, 'ContentRow');
const ContentColumn = themeStyles.apply(Column, 'ContentColumn');
const ContentCopy = themeStyles.apply(Copy, 'ContentCopy');
const Title = themeStyles.apply(Copy, 'Title');

const PrintedAccordion = ({ specifications, trimIds }) => {
  const { t } = useTranslation();

  const accordionItems = specifications.map(category => ({
    key: category?.name,
    roleLabel: 'none',
    expandAriaLabel: `${category?.label} ${t('Shared.Common.expandAria')}`,
    collapseAriaLabel: `${category?.label} ${t('Shared.Common.collapseAria')}`,
    title: (
      <Title size="accordionLarge" data-testid="cy-product-specification">
        {category?.label}
      </Title>
    ),
    content: category?.specifications?.map(specification => {
      const specItemsArr = specification?.specs;
      let specItemsWithPlaceholder = specItemsArr;
      /*
      When num of spec items is less than MAX_COMPARE_ELEMENTS,
      match item according to the position of trimId and fill empty one
      */
      if (Array.isArray(specItemsArr) && specItemsArr.length < MAX_COMPARE_ELEMENTS) {
        specItemsWithPlaceholder = trimIds.map(trimId => {
          const matchingSpecItem = specItemsArr?.find(specItem => specItem.trimId?.toString() === trimId);
          // when a match is found, return item, if not, matchingSpecItem is undefined
          return matchingSpecItem;
        });
      }

      const specItems = specItemsWithPlaceholder?.map((item, itemIndex) => (
        <ContentColumn width={[1 / 5]} key={`${specification?.name}-${item?.trimId ?? itemIndex}`}>
          <ContentCopy
            size="small"
            textAlign={item?.value === SPECS_ACCORDION_EMPTY_CONTENT ? 'center' : 'left'}
            isSingleColumn={category.specifications?.length === 1}
          >
            {compiler(stripMarkdownHeading(item?.value))}
          </ContentCopy>
        </ContentColumn>
      ));

      return (
        <Fragment key={specification?.name}>
          <ContentRow>
            <ContentColumn width={1 / 5}>
              <ContentCopy size="small" fontFamily="default">
                {compiler(stripMarkdownHeading(specification?.label))}
              </ContentCopy>
            </ContentColumn>
            {specItems}
          </ContentRow>
        </Fragment>
      );
    }),
  }));

  const accordionBehaviour = useAccordion(accordionItems);
  const printKeys = accordionItems.map(({ key }) => key);
  const { toggleAllItems } = accordionBehaviour;

  useEffect(() => {
    toggleAllItems();
  }, []);

  return (
    <Wrapper data-testid="cy-product-spec-wrapper">
      <Content>
        <Accordion
          ariaExpanded="false"
          behaviour={accordionBehaviour}
          items={accordionItems}
          defaultActiveItems={printKeys}
          isCompact
          itemsHaveTopBorder
          isTitleSticky
          colorStyling="default"
        />
      </Content>
    </Wrapper>
  );
};

PrintedAccordion.propTypes = {
  trimIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  specifications: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default PrintedAccordion;
